// @tennis-matches/services/winterdoppel/sessions

import { getSupabaseClient } from "../supabase/index.js";
import {
  generateSessionDates,
  playerAvailableOnDate,
  toIsoDate,
} from "./dateUtils.js";
import { isDateBlocked } from "./blocks.js";
import {
  assignCourtsAndPairs,
  assignTrainingGroupCourts,
  buildPartnerHistoryCounts,
  buildPlayerLevelMap,
  recordPartnerAssignments,
} from "./pairing.js";
import {
  loadWinterDoppelPlayers,
  loadWinterDoppelPlayerBlocks,
} from "./players.js";
import {
  buildWinterDoppelAvailabilityByPlayer,
  buildWinterDoppelFairnessContext,
} from "./fairness.js";
import {
  participationCapForFairness,
  participationSlotAllowed,
  recordParticipationAssignment,
  seedParticipationStateFromAssignments,
} from "./participation.js";
import { getWinterDoppelSeries } from "./series.js";
import { markWinterDoppelSeriesDraftAutofill } from "./draftAutofill.js";
import { syncWinterDoppelSeriesToTrainingsDesk } from "./trainingsDeskSync.js";
import { finalizeWinterDoppelSeries } from "./series.js";
import { loadWinterDoppelSeriesBilling } from "./billing.js";
import {
  assertWinterDoppelSeasonCostCoverage,
  listPostPublishJoinersReadyForSeasonCost,
  redistributeCostsForPublishedPlayerJoin,
} from "./costs.js";
import {
  assertWinterDoppelPlanEditable,
  assertWinterDoppelSessionStatusAllowed,
  isWinterDoppelPlanFinal,
} from "./planLock.js";
import {
  applyForcedBondsToStarterSelection,
  applyForcedGroupsToStarterSelection,
  applyLateJoiner,
  applyReservePromote,
  applyStarterDecline,
  applyStarterUndecline,
  assignAllDraftCostShares,
  demoteExcessUnpairedStarters,
  eveningTotal,
  fillWinterDoppelPairingGaps,
  isSeasonAboMode,
  normalizeWinterDoppelSessionCourtMix,
  playingStarters,
  pickStartersAndWaitlistByQuote,
  pickTrainingGroupsForSession,
  splitCostShares,
  assignDraftCostShares,
} from "./planning.js";
import { applyWinterDoppelSessionCostPreset as applySessionCostPresetRows } from "./sessionCostShares.js";
import { normalizeWinterDoppelFairnessMode } from "./constants.js";
import { bondsToForcedPairs, loadWinterDoppelPlayerBonds } from "./bonds.js";
import {
  loadWinterDoppelPlayerGroups,
  playerGroupsToForcedIds,
  buildWinterDoppelGroupScheduleIndex,
  resolveWinterDoppelTrainingSessionDates,
  generateWinterDoppelGroupDates,
  withWinterDoppelAlternatingGroupPhases,
} from "./groups.js";
import {
  applyWinterDoppelDateOverrides,
  loadWinterDoppelDateOverrides,
} from "./dateOverrides.js";
import {
  diffTrainingSessionPlayers,
  expectedTrainingPlayerIdsForDate,
  nextWinterDoppelOneOffWeekSwapPairs,
  trainingGroupSchedulesFromGroups,
} from "./publishedTraining.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

const SESSION_SELECT =
  "id,tenant_id,created_by,series_id,session_date,status,notes,created_at,updated_at";

const SP_SELECT =
  "id,tenant_id,session_id,player_id,role,rsvp,court_no,pair_slot,waitlist_rank,cost_share,credit_share,substitute_for_player_id,created_at,updated_at";

async function loadForcedPlanningConstraints(series) {
  const mode = __asStr(series?.series_mode || series?.seriesMode) || "free";
  if (mode === "training") {
    const groups = await loadWinterDoppelPlayerGroups(series.id);
    return {
      forcedPairs: [],
      forcedGroups: playerGroupsToForcedIds(groups),
    };
  }
  const bonds = await loadWinterDoppelPlayerBonds(series.id);
  return {
    forcedPairs: bondsToForcedPairs(bonds),
    forcedGroups: [],
  };
}

function applyPairingToRows(
  rows,
  courtCount,
  partnerHistory,
  preferredPairs = [],
  forcedGroups = [],
  playerLevels = new Map(),
) {
  const playing = playingStarters(rows);
  const asg = assignCourtsAndPairs({
    playerIds: playing.map((p) => p.player_id),
    courtCount,
    partnerHistory,
    preferredPairs,
    forcedGroups,
    playerLevels,
  });
  for (const sp of rows) {
    if (sp.role === "starter" && sp.rsvp !== "no") {
      const a = asg.get(String(sp.player_id));
      sp.court_no = a?.court_no ?? null;
      sp.pair_slot = a?.pair_slot ?? null;
    } else {
      sp.court_no = null;
      sp.pair_slot = null;
    }
  }
  recordPartnerAssignments(partnerHistory, asg);
}

const PERSIST_CHUNK = 80;

async function deleteSessionPlayersByIds(ids) {
  const list = (Array.isArray(ids) ? ids : []).map(__asStr).filter(Boolean);
  if (!list.length) return;
  const supabase = await getSupabaseClient();
  for (let i = 0; i < list.length; i += PERSIST_CHUNK) {
    const { error } = await supabase
      .from("winterdoppel_session_players")
      .delete()
      .in("id", list.slice(i, i + PERSIST_CHUNK));
    if (error) throw error;
  }
}

async function insertSessionPlayerRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return;
  const supabase = await getSupabaseClient();
  for (let i = 0; i < list.length; i += PERSIST_CHUNK) {
    const { error } = await supabase
      .from("winterdoppel_session_players")
      .insert(list.slice(i, i + PERSIST_CHUNK));
    if (error) throw error;
  }
}

function sessionPlayerPatch(row, fields = null) {
  if (fields === "costs") {
    return {
      waitlist_rank: row.waitlist_rank,
      cost_share: row.cost_share,
      credit_share: row.credit_share,
      substitute_for_player_id: row.substitute_for_player_id,
    };
  }
  return {
    role: row.role,
    rsvp: row.rsvp,
    court_no: row.court_no,
    pair_slot: row.pair_slot,
    waitlist_rank: row.waitlist_rank,
    cost_share: row.cost_share,
    credit_share: row.credit_share,
    substitute_for_player_id: row.substitute_for_player_id,
  };
}

function sessionPlayerPatchChanged(before, after, fields = null) {
  if (!before) return true;
  const a = sessionPlayerPatch(after, fields);
  if (fields === "costs") {
    return (
      before.waitlist_rank !== a.waitlist_rank ||
      Number(before.cost_share) !== Number(a.cost_share) ||
      Number(before.credit_share) !== Number(a.credit_share) ||
      String(before.substitute_for_player_id || "") !==
        String(a.substitute_for_player_id || "")
    );
  }
  return (
    before.role !== a.role ||
    before.rsvp !== a.rsvp ||
    before.court_no !== a.court_no ||
    before.pair_slot !== a.pair_slot ||
    before.waitlist_rank !== a.waitlist_rank ||
    Number(before.cost_share) !== Number(a.cost_share) ||
    Number(before.credit_share) !== Number(a.credit_share) ||
    String(before.substitute_for_player_id || "") !==
      String(a.substitute_for_player_id || "")
  );
}

function patchGroupKey(patch) {
  return JSON.stringify(patch);
}

async function persistSessionPlayerRows(
  nextRows,
  { previousById = null, fields = null } = {},
) {
  const rows = (Array.isArray(nextRows) ? nextRows : []).filter((row) => {
    if (!row?.id) return false;
    if (!previousById) return true;
    return sessionPlayerPatchChanged(previousById.get(row.id), row, fields);
  });
  if (!rows.length) return;

  const groups = new Map();
  for (const row of rows) {
    const patch = sessionPlayerPatch(row, fields);
    const key = patchGroupKey(patch);
    let group = groups.get(key);
    if (!group) {
      group = { patch, ids: [] };
      groups.set(key, group);
    }
    group.ids.push(row.id);
  }

  const supabase = await getSupabaseClient();
  const jobs = [];
  for (const { patch, ids } of groups.values()) {
    for (let i = 0; i < ids.length; i += PERSIST_CHUNK) {
      jobs.push(
        supabase
          .from("winterdoppel_session_players")
          .update(patch)
          .in("id", ids.slice(i, i + PERSIST_CHUNK)),
      );
    }
  }
  const results = await Promise.all(jobs);
  for (const { error } of results) {
    if (error) throw error;
  }
}

function billingCostOpts(series, billing, sessionCount) {
  return {
    hourlyRate: Number(billing?.hourly_rate) || 0,
    startTime: series?.start_time,
    endTime: series?.end_time,
    sessionCount: Math.max(0, Math.floor(Number(sessionCount) || 0)),
  };
}

function activeSessionCount(sessions = []) {
  return (sessions || []).filter((s) => s && s.status !== "cancelled").length;
}

const IN_QUERY_CHUNK = 80;

async function fetchInChunks(ids, fetchChunk) {
  const unique = [
    ...new Set((Array.isArray(ids) ? ids : []).map(__asStr).filter(Boolean)),
  ];
  if (!unique.length) return [];
  const parts = [];
  for (let i = 0; i < unique.length; i += IN_QUERY_CHUNK) {
    parts.push(fetchChunk(unique.slice(i, i + IN_QUERY_CHUNK)));
  }
  const rows = await Promise.all(parts);
  return rows.flat();
}

export async function loadWinterDoppelSessions(seriesId, opts = {}) {
  const supabase = await getSupabaseClient();
  let q = supabase
    .from("winterdoppel_sessions")
    .select(SESSION_SELECT)
    .eq("series_id", __asStr(seriesId))
    .order("session_date", { ascending: true });
  const fromDate = __asStr(opts.fromDate).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(fromDate)) {
    q = q.gte("session_date", fromDate);
  }
  const { data, error } = await q;
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

/**
 * Einzelne Winterdoppel-Session per ID (für Hub-Deep-Links).
 * @param {string} sessionId
 * @returns {Promise<object|null>}
 */
export async function getWinterDoppelSessionById(sessionId) {
  const id = __asStr(sessionId);
  if (!id) return null;
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_sessions")
    .select(SESSION_SELECT)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

/** Sessions für viele Serien in wenigen Roundtrips (optional ab fromDate / bis toDate). */
export async function loadWinterDoppelSessionsForSeriesIds(
  seriesIds = [],
  opts = {},
) {
  const fromDate = __asStr(opts.fromDate).slice(0, 10);
  const toDate = __asStr(opts.toDate).slice(0, 10);
  const hasFrom = /^\d{4}-\d{2}-\d{2}$/.test(fromDate);
  const hasTo = /^\d{4}-\d{2}-\d{2}$/.test(toDate);
  return fetchInChunks(seriesIds, async (chunk) => {
    const supabase = await getSupabaseClient();
    let q = supabase
      .from("winterdoppel_sessions")
      .select(SESSION_SELECT)
      .in("series_id", chunk)
      .order("session_date", { ascending: true });
    if (hasFrom) q = q.gte("session_date", fromDate);
    if (hasTo) q = q.lte("session_date", toDate);
    const { data, error } = await q;
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  });
}

export async function loadWinterDoppelSessionPlayers(sessionIds = []) {
  return fetchInChunks(sessionIds, async (chunk) => {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("winterdoppel_session_players")
      .select(SP_SELECT)
      .in("session_id", chunk);
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  });
}

export async function normalizeWinterDoppelDraftSessionStatuses(seriesId) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series || isWinterDoppelPlanFinal(series)) return 0;
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_sessions")
    .update({ status: "proposed" })
    .eq("series_id", __asStr(seriesId))
    .in("status", ["asked", "confirmed"])
    .select("id");
  if (error) throw error;
  return Array.isArray(data) ? data.length : 0;
}

/** Entwurf: alte rsvp=no aus Testzeiten/Wochen-Check-in zurücksetzen. */
export async function clearWinterDoppelDraftRsvps(seriesId) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series || isWinterDoppelPlanFinal(series)) return 0;
  const sessions = await loadWinterDoppelSessions(seriesId);
  const sessionIds = sessions.map((s) => s.id).filter(Boolean);
  if (!sessionIds.length) return 0;
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_session_players")
    .update({ rsvp: "pending" })
    .in("session_id", sessionIds.map(__asStr))
    .eq("rsvp", "no")
    .select("id");
  if (error) throw error;
  return Array.isArray(data) ? data.length : 0;
}

/** @param {unknown} raw */
export function normalizeWinterDoppelSessionDate(raw) {
  const d = String(raw ?? "")
    .trim()
    .slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return "";
  return d;
}

/**
 * Prüft, ob das Datum in der Serie noch frei ist (aktive Sessions).
 * @param {{
 *   sessionId: string,
 *   seriesId: string,
 *   sessionDate: string,
 *   existingSessions?: object[],
 * }} input
 */
export function assertWinterDoppelSessionDateAvailable({
  sessionId,
  seriesId,
  sessionDate,
  existingSessions = [],
}) {
  const date = normalizeWinterDoppelSessionDate(sessionDate);
  if (!date) {
    throw new Error("winterdoppel.trainingBriefing.reschedule.invalidDate");
  }
  const sid = __asStr(sessionId);
  const series = __asStr(seriesId);
  const conflict = (
    Array.isArray(existingSessions) ? existingSessions : []
  ).find(
    (s) =>
      __asStr(s?.id) &&
      __asStr(s.id) !== sid &&
      __asStr(s.series_id || series) === series &&
      normalizeWinterDoppelSessionDate(s.session_date) === date &&
      __asStr(s.status) !== "cancelled",
  );
  if (conflict) {
    throw new Error("winterdoppel.trainingBriefing.reschedule.dateConflict");
  }
  return date;
}

export async function updateWinterDoppelSession(id, updates = {}) {
  const sessionId = __asStr(id);
  const wantsStatus = updates.status != null;
  const wantsDate = updates.session_date != null || updates.sessionDate != null;
  const wantsNotes = updates.notes != null;

  let seriesId = "";
  let series = null;
  if (wantsStatus || wantsDate) {
    const supabase = await getSupabaseClient();
    const { data: session, error: readErr } = await supabase
      .from("winterdoppel_sessions")
      .select("id,series_id,session_date,status")
      .eq("id", sessionId)
      .maybeSingle();
    if (readErr) throw readErr;
    if (!session) throw new Error("winterdoppel.plan.notFound");
    seriesId = __asStr(session.series_id);
    series = await getWinterDoppelSeries(seriesId);
    if (!series) throw new Error("winterdoppel.series.notFound");
    if (wantsStatus) {
      assertWinterDoppelSessionStatusAllowed(series, updates.status);
    }
  }

  const patch = {};
  if (wantsStatus) patch.status = __asStr(updates.status);
  if (wantsNotes) patch.notes = __asStr(updates.notes) || null;
  if (wantsDate) {
    const nextDate = normalizeWinterDoppelSessionDate(
      updates.session_date ?? updates.sessionDate,
    );
    const siblings = await loadWinterDoppelSessions(seriesId);
    assertWinterDoppelSessionDateAvailable({
      sessionId,
      seriesId,
      sessionDate: nextDate,
      existingSessions: siblings,
    });
    patch.session_date = nextDate;
  }

  if (!Object.keys(patch).length) {
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase
      .from("winterdoppel_sessions")
      .select(SESSION_SELECT)
      .eq("id", sessionId)
      .single();
    if (error) throw error;
    return data;
  }

  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_sessions")
    .update(patch)
    .eq("id", sessionId)
    .select(SESSION_SELECT)
    .single();
  if (error) throw error;

  if (wantsDate && seriesId && __asStr(series?.series_mode) === "training") {
    try {
      await syncWinterDoppelSeriesToTrainingsDesk(seriesId, {
        updateExisting: true,
      });
    } catch (_) {
      /* Desk-Sync best-effort; Datum in WinterDoppel ist gespeichert */
    }
  }

  return data;
}

export async function updateWinterDoppelSessionPlayer(id, updates = {}) {
  if (updates.rsvp === "no") {
    const supabase = await getSupabaseClient();
    const { data: current, error: readErr } = await supabase
      .from("winterdoppel_session_players")
      .select(SP_SELECT)
      .eq("id", __asStr(id))
      .maybeSingle();
    if (readErr) throw readErr;
    if (current?.role === "starter") {
      return declineWinterDoppelStarter(current.id);
    }
  }

  if (updates.rsvp === "yes" || updates.rsvp === "pending") {
    const supabase = await getSupabaseClient();
    const { data: current, error: readErr } = await supabase
      .from("winterdoppel_session_players")
      .select(SP_SELECT)
      .eq("id", __asStr(id))
      .maybeSingle();
    if (readErr) throw readErr;
    if (
      (current?.role === "starter" || current?.role === "reserve") &&
      current.rsvp === "no"
    ) {
      return undeclineWinterDoppelStarter(current.id, {
        rsvp: __asStr(updates.rsvp),
      });
    }
  }

  const supabase = await getSupabaseClient();
  const patch = {};
  if (updates.role != null) patch.role = __asStr(updates.role);
  if (updates.rsvp != null) patch.rsvp = __asStr(updates.rsvp);
  if (updates.court_no !== undefined) patch.court_no = updates.court_no;
  if (updates.pair_slot !== undefined) patch.pair_slot = updates.pair_slot;
  if (updates.waitlist_rank !== undefined) {
    patch.waitlist_rank = updates.waitlist_rank;
  }
  if (updates.cost_share !== undefined) patch.cost_share = updates.cost_share;
  if (patch.role === "reserve") {
    patch.court_no = null;
    patch.pair_slot = null;
  }
  const { data, error } = await supabase
    .from("winterdoppel_session_players")
    .update(patch)
    .eq("id", __asStr(id))
    .select(SP_SELECT)
    .single();
  if (error) throw error;
  return data;
}

/**
 * Erzeugt fehlende Session-Zeilen für Serien-Termine.
 * Training mit Gruppen: nur Gruppen-Termine (Intervall + Ferien); überzählige Draft-Termine entfernen.
 */
export async function ensureWinterDoppelSessions(seriesId, opts = {}) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series) throw new Error("winterdoppel.series.notFound");
  assertWinterDoppelPlanEditable(series, opts);

  const mode = __asStr(series.series_mode || series.seriesMode) || "free";
  const overrides = await loadWinterDoppelDateOverrides(seriesId);
  let dates;
  if (mode === "training") {
    const groups = await loadWinterDoppelPlayerGroups(seriesId);
    dates = await resolveWinterDoppelTrainingSessionDates(
      series,
      groups,
      overrides,
    );
  } else {
    const weekly = generateSessionDates({
      seasonStart: series.season_start,
      seasonEnd: series.season_end,
      weekdays: series.weekdays,
    });
    dates = applyWinterDoppelDateOverrides(weekly, overrides, weekly);
  }

  const existing = await loadWinterDoppelSessions(seriesId);
  const want = new Set(dates);
  const have = new Set(existing.map((s) => s.session_date));
  const missing = dates.filter((d) => !have.has(d));

  const supabase = await getSupabaseClient();

  // Draft-Termine außerhalb der Soll-Liste entfernen (Ferien, force_off, …)
  const protectBefore = String(opts.protectBefore || "").slice(0, 10);
  const orphans = existing.filter((s) => {
    const d = String(s.session_date || "").slice(0, 10);
    if (protectBefore && d && d < protectBefore) return false;
    return (
      s?.id && (s.status === "proposed" || s.status === "asked") && !want.has(d)
    );
  });
  if (orphans.length) {
    const orphanIds = orphans.map((s) => s.id);
    const sps = await loadWinterDoppelSessionPlayers(orphanIds);
    if (sps.length) {
      await deleteSessionPlayersByIds(sps.map((sp) => sp.id).filter(Boolean));
    }
    const { error: delErr } = await supabase
      .from("winterdoppel_sessions")
      .delete()
      .in("id", orphanIds);
    if (delErr) throw delErr;
  }

  if (missing.length) {
    const rows = missing.map((session_date) => {
      const row = {
        series_id: series.id,
        session_date,
        status: "proposed",
      };
      if (series.tenant_id) row.tenant_id = series.tenant_id;
      return row;
    });
    const { error } = await supabase.from("winterdoppel_sessions").insert(rows);
    if (error) throw error;
  }
  return loadWinterDoppelSessions(seriesId);
}

/**
 * Grobplanung: Starter nach Quote + Warteliste + Kosten.
 * Im Entwurf werden alte RSVP-Absagen verworfen (gelten erst nach Finalize).
 *
 * @param {string} seriesId
 * @param {{ skipSessionPrep?: boolean }} [opts]
 */
export async function autofillWinterDoppelSeries(seriesId, opts = {}) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series) throw new Error("winterdoppel.series.notFound");
  if (!opts.allowPublished) {
    assertWinterDoppelPlanEditable(series);
  }
  if (!opts.skipSessionPrep) {
    await normalizeWinterDoppelDraftSessionStatuses(seriesId);
    await clearWinterDoppelDraftRsvps(seriesId);
  }
  const billing = await loadWinterDoppelSeriesBilling(seriesId);
  const courtFee = Number(billing?.court_fee) || 0;

  const sessions = await ensureWinterDoppelSessions(seriesId);
  const players = await loadWinterDoppelPlayers(seriesId);
  const mode = __asStr(series.series_mode || series.seriesMode) || "free";
  const trainingGroups =
    mode === "training" ? await loadWinterDoppelPlayerGroups(seriesId) : [];
  /** @type {Array<{ group: object, playerIds: string[], dates: Set<string> }>} */
  let groupSchedules = [];
  if (mode === "training" && trainingGroups.length) {
    const phased = withWinterDoppelAlternatingGroupPhases(
      trainingGroups.map((g) => ({
        ...g,
        schedule_end: g.schedule_end || series.season_end,
        season_end: series.season_end,
      })),
    );
    // Rhythmus ohne Ferienfilter: force_on-Ferientage bleiben für die passende Gruppe spielbar
    const withDates = phased.map((g) => ({
      ...g,
      dates: generateWinterDoppelGroupDates({
        ...g,
        exclude_holidays: false,
      }),
    }));
    groupSchedules = buildWinterDoppelGroupScheduleIndex(withDates);
  }
  const { forcedPairs, forcedGroups } =
    await loadForcedPlanningConstraints(series);
  const blocks = await loadWinterDoppelPlayerBlocks(players.map((p) => p.id));
  const blocksByPlayer = new Map();
  for (const b of blocks) {
    const list = blocksByPlayer.get(b.player_id) || [];
    list.push(b);
    blocksByPlayer.set(b.player_id, list);
  }

  const allSp = await loadWinterDoppelSessionPlayers(sessions.map((s) => s.id));

  const partnerHistory = buildPartnerHistoryCounts(allSp);
  const courtCount = Math.max(1, Number(series.court_count) || 1);
  const fairnessMode = normalizeWinterDoppelFairnessMode(
    series.fairness_mode ?? series.fairnessMode,
  );
  const costOpts = billingCostOpts(
    series,
    billing,
    activeSessionCount(sessions),
  );
  const sessionDates = sessions
    .filter((s) => s.status !== "cancelled")
    .map((s) => String(s.session_date || "").slice(0, 10))
    .filter(Boolean);
  const availabilityCounts = buildWinterDoppelAvailabilityByPlayer({
    players,
    sessions,
    blocks,
  });
  const participationCaps = new Map(
    players
      .filter((p) => p?.id)
      .map((p) => [String(p.id), participationCapForFairness(p, sessionDates)]),
  );
  const playerLevels = buildPlayerLevelMap(players);

  const fillable = sessions
    .filter((s) => s.status === "proposed" || s.status === "asked")
    .sort((a, b) =>
      String(a.session_date || "").localeCompare(String(b.session_date || "")),
    );
  const fillableIds = new Set(fillable.map((s) => s.id));
  const sessionsById = new Map(sessions.map((s) => [String(s.id), s]));
  const participationState = seedParticipationStateFromAssignments(
    allSp.filter((sp) => !fillableIds.has(sp.session_id)),
    sessionsById,
  );
  const starterCounts = participationState.starterCounts;

  const rowsToInsert = [];

  for (const session of fillable) {
    const sessionDate = String(session.session_date || "").slice(0, 10);

    const candidates = players
      .filter((p) => p.interest_status !== "no")
      .filter((p) =>
        playerAvailableOnDate(
          p,
          blocksByPlayer.get(p.id) || [],
          session.session_date,
        ),
      )
      .filter((p) =>
        participationSlotAllowed(
          p,
          session.session_date,
          participationState,
          sessionDates,
        ),
      );

    let starters;
    let reserves;
    if (mode === "training" && groupSchedules.length) {
      const onDateIds = new Set();
      for (const gs of groupSchedules) {
        if (!gs.dates.has(sessionDate)) continue;
        for (const id of gs.playerIds || []) onDateIds.add(String(id));
      }
      const dayCandidates = candidates.filter((p) =>
        onDateIds.has(String(p.id)),
      );
      const availableIds = new Set(dayCandidates.map((p) => String(p.id)));
      const pickedGroups = pickTrainingGroupsForSession({
        groupSchedules,
        sessionDate,
        courtCount,
        starterCounts,
        availablePlayerIds: availableIds,
      });
      const byId = new Map(dayCandidates.map((p) => [String(p.id), p]));
      starters = pickedGroups.starterIds
        .map((id) => byId.get(id))
        .filter(Boolean);
      reserves = [];
    } else {
      const picked = pickStartersAndWaitlistByQuote(
        candidates,
        courtCount,
        starterCounts,
        availabilityCounts,
        Math.random,
        participationCaps,
        fairnessMode,
      );
      const withGroups = applyForcedGroupsToStarterSelection({
        starters: picked.starters,
        reserves: picked.reserves,
        forcedGroups,
        courtCount,
      });
      const withBonds = applyForcedBondsToStarterSelection({
        starters: withGroups.starters,
        reserves: withGroups.reserves,
        forcedPairs,
        courtCount,
      });
      starters = withBonds.starters;
      reserves = withBonds.reserves;
    }
    for (const p of starters) {
      recordParticipationAssignment(
        p.id,
        session.session_date,
        participationState,
      );
    }
    const dayGroupSchedules =
      mode === "training" && groupSchedules.length
        ? groupSchedules.filter((gs) => gs.dates.has(sessionDate))
        : [];
    const pairAssignments =
      mode === "training" && dayGroupSchedules.length
        ? assignTrainingGroupCourts({
            groupSchedules: dayGroupSchedules,
            starterIds: starters.map((p) => p.id),
            courtCount,
            playerLevels,
            preferredPairs: forcedPairs,
          })
        : assignCourtsAndPairs({
            playerIds: starters.map((p) => p.id),
            courtCount,
            partnerHistory,
            preferredPairs: forcedPairs,
            forcedGroups,
            playerLevels,
          });

    if (mode !== "training") {
      recordPartnerAssignments(partnerHistory, pairAssignments);
    }

    const assignedStarters = [];
    const unpairedToReserve = [];
    for (const p of starters) {
      if (pairAssignments.has(String(p.id))) assignedStarters.push(p);
      else unpairedToReserve.push(p);
    }
    const dayReserves =
      mode === "training" ? [] : [...unpairedToReserve, ...reserves];

    rowsToInsert.push(
      ...assignedStarters.map((p) => {
        const asg = pairAssignments.get(String(p.id));
        const courtNo = asg?.court_no ?? null;
        let pairSlot = asg?.pair_slot ?? null;
        if (courtNo != null && pairSlot != null) {
          pairSlot = Math.min(1, Math.max(0, Number(pairSlot) || 0));
        } else {
          pairSlot = null;
        }
        return {
          session_id: session.id,
          player_id: p.id,
          role: "starter",
          rsvp: "pending",
          court_no: courtNo,
          pair_slot: courtNo == null ? null : pairSlot,
          waitlist_rank: null,
          cost_share: 0,
          ...(series.tenant_id ? { tenant_id: series.tenant_id } : {}),
        };
      }),
      ...dayReserves.map((p) => ({
        session_id: session.id,
        player_id: p.id,
        role: "reserve",
        rsvp: "pending",
        court_no: null,
        pair_slot: null,
        waitlist_rank: null,
        cost_share: 0,
        ...(series.tenant_id ? { tenant_id: series.tenant_id } : {}),
      })),
    );
  }

  // Pro Termin ersetzen (nicht erst alles löschen): bei Insert-Fehler bleibt
  // der Rest der Serie erhalten statt komplett leer.
  const rowsBySession = new Map();
  for (const row of rowsToInsert) {
    const sid = String(row.session_id || "");
    if (!sid) continue;
    const list = rowsBySession.get(sid) || [];
    list.push(row);
    rowsBySession.set(sid, list);
  }
  const existingBySession = new Map();
  for (const sp of allSp) {
    const sid = String(sp.session_id || "");
    if (!sid || !sp?.id) continue;
    const list = existingBySession.get(sid) || [];
    list.push(sp.id);
    existingBySession.set(sid, list);
  }
  for (const session of fillable) {
    const sid = String(session.id);
    const nextRows = rowsBySession.get(sid) || [];
    for (const row of nextRows) {
      if (row.pair_slot != null && row.pair_slot !== 0 && row.pair_slot !== 1) {
        throw new Error("winterdoppel.plan.invalidPairSlot");
      }
    }
    await deleteSessionPlayersByIds(existingBySession.get(sid) || []);
    await insertSessionPlayerRows(nextRows);
  }

  const nextSp = await loadWinterDoppelSessionPlayers(
    sessions.map((s) => s.id),
  );
  assignAllDraftCostShares(nextSp, courtFee, courtCount, costOpts);
  await persistSessionPlayerRows(nextSp, { fields: "costs" });
  await markWinterDoppelSeriesDraftAutofill(seriesId);
  return { sessions, sessionPlayers: nextSp, players };
}

/** Grobplanung neu, wenn Serie noch im Entwurf (z. B. nach Abwesenheits-Änderung). */
export async function refreshWinterDoppelDraftPlan(seriesId, opts = {}) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series || isWinterDoppelPlanFinal(series)) return false;
  await autofillWinterDoppelSeries(seriesId, opts);
  return true;
}

/**
 * Unterbesetzung nachziehen: Solo-/3er-Plätze → Mix (Einzel + Warteliste).
 * RSVPs bleiben; Kosten vom Demotierten gehen auf die verbleibenden Starter.
 * @returns {{ changedSessions: number }}
 */
export async function repairWinterDoppelSeriesCourtMix(seriesId) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series) return { changedSessions: 0 };
  if (String(series.series_mode || "free") === "training") {
    return { changedSessions: 0 };
  }

  const courtCount = Math.max(1, Number(series.court_count) || 1);
  const locked = isWinterDoppelPlanFinal(series);
  const billing = await loadWinterDoppelSeriesBilling(series.id);
  const sessions = await loadWinterDoppelSessions(series.id);
  const allSp = await loadWinterDoppelSessionPlayers(
    sessions.map((s) => s.id).filter(Boolean),
  );
  const previousById = new Map(allSp.map((sp) => [sp.id, { ...sp }]));
  const bySession = new Map();
  for (const sp of allSp) {
    const sid = String(sp.session_id || "");
    if (!sid) continue;
    const list = bySession.get(sid) || [];
    list.push(sp);
    bySession.set(sid, list);
  }

  let changedSessions = 0;
  for (const session of sessions) {
    if (!session?.id || session.status === "cancelled") continue;
    const rows = bySession.get(String(session.id)) || [];
    if (!rows.length) continue;

    const before = new Map(
      rows.map((sp) => [
        sp.id,
        { role: sp.role, cost_share: Number(sp.cost_share) || 0 },
      ]),
    );
    const did = normalizeWinterDoppelSessionCourtMix(rows, courtCount);
    if (!did) continue;
    changedSessions += 1;

    if (locked) {
      for (const sp of rows) {
        const prev = before.get(sp.id);
        if (!prev || prev.role !== "starter" || sp.role !== "reserve") continue;
        const amount = Math.round(prev.cost_share * 100) / 100;
        sp.cost_share = 0;
        const payers = playingStarters(rows);
        if (amount <= 0 || !payers.length) continue;
        const parts = splitCostShares(amount, payers.length);
        payers.forEach((p, i) => {
          p.cost_share =
            Math.round(((Number(p.cost_share) || 0) + (parts[i] || 0)) * 100) /
            100;
        });
      }
    } else {
      assignDraftCostShares(
        rows,
        Number(billing?.court_fee) || 0,
        courtCount,
        billingCostOpts(series, billing, activeSessionCount(sessions)),
      );
    }
  }

  if (changedSessions > 0) {
    await persistSessionPlayerRows(allSp, { previousById });
  }
  return { changedSessions };
}

/**
 * Nach Veröffentlichung: Abwesenheiten punktuell in die Termin-Zuordnung übernehmen
 * und nur für wieder freigegebene Absenz-Tage zurücksetzen.
 * Kosten-only-Zeilen vom Publish-Neuzugang (reserve + rsvp=no + cost_share) bleiben unberührt.
 * @param {string} playerId
 * @param {{ previousBlocks?: object[]|null }} [opts]
 * @returns {{ changed: number }}
 */
export async function applyWinterDoppelFinalPlanAbsencesFromBlocks(
  playerId,
  { previousBlocks = null } = {},
) {
  const pid = __asStr(playerId);
  if (!pid) return { changed: 0 };

  const supabase = await getSupabaseClient();
  const { data: player, error } = await supabase
    .from("winterdoppel_players")
    .select("id,series_id")
    .eq("id", pid)
    .maybeSingle();
  if (error) throw error;
  if (!player?.series_id) return { changed: 0 };

  const series = await getWinterDoppelSeries(player.series_id);
  if (!series || !isWinterDoppelPlanFinal(series)) return { changed: 0 };

  const blocks = await loadWinterDoppelPlayerBlocks([pid]);
  const sessions = await loadWinterDoppelSessions(series.id);
  const allSp = await loadWinterDoppelSessionPlayers(sessions.map((s) => s.id));
  let changed = 0;

  for (const session of sessions) {
    if (!session || session.status === "cancelled") continue;
    const date = String(session.session_date || "").slice(0, 10);
    if (!date) continue;
    const blocked = isDateBlocked(blocks, date);
    const wasBlocked =
      previousBlocks != null && isDateBlocked(previousBlocks, date);
    const newlyUnblocked = wasBlocked && !blocked;

    if (blocked) {
      const sp = allSp.find(
        (row) =>
          String(row.session_id) === String(session.id) &&
          String(row.player_id) === pid &&
          row.rsvp !== "no",
      );
      if (!sp?.id) continue;

      if (sp.role === "starter") {
        await declineWinterDoppelStarter(sp.id);
        changed += 1;
        continue;
      }

      if (sp.role === "reserve") {
        await updateWinterDoppelSessionPlayer(sp.id, {
          rsvp: "no",
          waitlist_rank: null,
          court_no: null,
          pair_slot: null,
        });
        changed += 1;
      }
      continue;
    }

    if (!newlyUnblocked) continue;

    const declined = allSp.find(
      (row) =>
        String(row.session_id) === String(session.id) &&
        String(row.player_id) === pid &&
        row.rsvp === "no",
    );
    if (!declined?.id) continue;

    if (declined.role === "starter") {
      await undeclineWinterDoppelStarter(declined.id);
      changed += 1;
      continue;
    }

    if (declined.role === "reserve") {
      const cost = Math.round((Number(declined.cost_share) || 0) * 100) / 100;
      if (cost > 0) continue;
      await updateWinterDoppelSessionPlayer(declined.id, {
        rsvp: "pending",
      });
      changed += 1;
    }
  }

  return { changed };
}

async function mutateSessionPlayers(
  sessionPlayerId,
  mutator,
  { mutatorArgs = {} } = {},
) {
  const supabase = await getSupabaseClient();
  const { data: current, error: readErr } = await supabase
    .from("winterdoppel_session_players")
    .select(SP_SELECT)
    .eq("id", __asStr(sessionPlayerId))
    .maybeSingle();
  if (readErr) throw readErr;
  if (!current) throw new Error("winterdoppel.plan.notFound");

  const { data: session, error: sessErr } = await supabase
    .from("winterdoppel_sessions")
    .select(SESSION_SELECT)
    .eq("id", current.session_id)
    .maybeSingle();
  if (sessErr) throw sessErr;
  if (!session) throw new Error("winterdoppel.plan.notFound");

  const series = await getWinterDoppelSeries(session.series_id);
  if (!series) throw new Error("winterdoppel.series.notFound");
  const billing = await loadWinterDoppelSeriesBilling(series.id);
  const seriesSessions = await loadWinterDoppelSessions(series.id);
  const players = await loadWinterDoppelPlayers(series.id);
  const blocks = await loadWinterDoppelPlayerBlocks(players.map((p) => p.id));
  const allSeriesSp = await loadWinterDoppelSessionPlayers(
    seriesSessions.map((s) => s.id),
  );
  const fairnessContext = buildWinterDoppelFairnessContext({
    players,
    sessions: seriesSessions,
    sessionPlayers: allSeriesSp,
    blocks,
    fairnessMode: series.fairness_mode ?? series.fairnessMode,
  });
  const { forcedPairs, forcedGroups } =
    await loadForcedPlanningConstraints(series);
  const rows = await loadWinterDoppelSessionPlayers([session.id]);
  const previousById = new Map(rows.map((sp) => [sp.id, { ...sp }]));
  const previousIds = new Set(rows.map((sp) => sp.id).filter(Boolean));
  const courtCount = Math.max(1, Number(series.court_count) || 1);
  const isTraining =
    __asStr(series.series_mode || series.seriesMode) === "training";
  const next = mutator(rows, {
    locked: isWinterDoppelPlanFinal(series),
    courtFee: Number(billing?.court_fee) || 0,
    courtCount,
    fairnessContext,
    skipCourtMix: isTraining,
    ...billingCostOpts(series, billing, activeSessionCount(seriesSessions)),
    ...mutatorArgs,
  });
  if (!isTraining) {
    demoteExcessUnpairedStarters(next, courtCount);
    fillWinterDoppelPairingGaps(next, courtCount);
    const stillNeedFullPairing = playingStarters(next).every((sp) => {
      const court = Number(sp.court_no);
      const slot = Number(sp.pair_slot);
      return !(
        Number.isFinite(court) &&
        court >= 1 &&
        (slot === 0 || slot === 1)
      );
    });
    if (stillNeedFullPairing && playingStarters(next).length > 0) {
      applyPairingToRows(
        next,
        courtCount,
        buildPartnerHistoryCounts(next),
        forcedPairs,
        forcedGroups,
        buildPlayerLevelMap(players),
      );
    }
  }
  const toInsert = next.filter((sp) => !sp?.id);
  if (toInsert.length) {
    const insertRows = toInsert.map((sp) => {
      const row = {
        session_id: session.id,
        player_id: sp.player_id,
        role: sp.role,
        rsvp: sp.rsvp || "pending",
        court_no: sp.court_no,
        pair_slot: sp.pair_slot,
        waitlist_rank: sp.waitlist_rank,
        cost_share: sp.cost_share ?? 0,
        credit_share: sp.credit_share ?? 0,
        substitute_for_player_id: sp.substitute_for_player_id ?? null,
      };
      if (series.tenant_id) row.tenant_id = series.tenant_id;
      return row;
    });
    await insertSessionPlayerRows(insertRows);
    const reloaded = await loadWinterDoppelSessionPlayers([session.id]);
    for (const sp of next.filter((row) => !row?.id)) {
      const saved = reloaded.find(
        (r) =>
          String(r.player_id) === String(sp.player_id) &&
          String(r.session_id) === String(session.id),
      );
      if (saved) sp.id = saved.id;
    }
  }
  await persistSessionPlayerRows(
    next.filter((sp) => sp?.id),
    { previousById },
  );

  const locked = isWinterDoppelPlanFinal(series);
  const costOpts = billingCostOpts(
    series,
    billing,
    activeSessionCount(seriesSessions),
  );
  if (
    !locked &&
    isSeasonAboMode(costOpts, Math.max(1, Number(series.court_count) || 1))
  ) {
    const allSp = await loadWinterDoppelSessionPlayers(
      seriesSessions.map((s) => s.id),
    );
    const previousCostsById = new Map(allSp.map((sp) => [sp.id, { ...sp }]));
    assignAllDraftCostShares(
      allSp,
      Number(billing?.court_fee) || 0,
      series.court_count,
      costOpts,
    );
    await persistSessionPlayerRows(allSp, {
      previousById: previousCostsById,
      fields: "costs",
    });
  }

  if (locked) {
    await maybeApplyPublishedJoinCostsAfterPlay(series.id);
  }

  return (
    next.find((r) => r.id === current.id) || next.find((r) => r.id) || current
  );
}

export async function declineWinterDoppelStarter(sessionPlayerId) {
  return mutateSessionPlayers(sessionPlayerId, (rows, opts) =>
    applyStarterDecline(rows, sessionPlayerId, opts),
  );
}

export async function undeclineWinterDoppelStarter(
  sessionPlayerId,
  { rsvp = null } = {},
) {
  return mutateSessionPlayers(sessionPlayerId, (rows, opts) =>
    applyStarterUndecline(rows, sessionPlayerId, { ...opts, rsvp }),
  );
}

export async function promoteWinterDoppelReserve(sessionPlayerId, opts = {}) {
  const replacesPlayerId = opts.replacesPlayerId ?? null;
  return mutateSessionPlayers(sessionPlayerId, (rows, mutOpts) =>
    applyReservePromote(rows, sessionPlayerId, {
      ...mutOpts,
      replacesPlayerId,
    }),
  );
}

export async function addWinterDoppelLateJoiner(
  sessionId,
  playerId,
  replacesPlayerId,
) {
  const sid = __asStr(sessionId);
  const pid = __asStr(playerId);
  const replaceId = __asStr(replacesPlayerId);
  if (!sid || !pid || !replaceId) {
    throw new Error("winterdoppel.lateJoiner.invalid");
  }

  const supabase = await getSupabaseClient();
  const { data: session, error: sessErr } = await supabase
    .from("winterdoppel_sessions")
    .select(SESSION_SELECT)
    .eq("id", sid)
    .maybeSingle();
  if (sessErr) throw sessErr;
  if (!session) throw new Error("winterdoppel.plan.notFound");

  const series = await getWinterDoppelSeries(session.series_id);
  if (!series) throw new Error("winterdoppel.series.notFound");
  if (!isWinterDoppelPlanFinal(series)) {
    throw new Error("winterdoppel.lateJoiner.needFinalPlan");
  }

  const players = await loadWinterDoppelPlayers(series.id);
  if (!players.some((p) => String(p.id) === pid)) {
    throw new Error("winterdoppel.lateJoiner.playerNotInSeries");
  }

  const rows = await loadWinterDoppelSessionPlayers([sid]);
  const existing = rows.find((sp) => String(sp.player_id) === pid);
  if (existing?.role === "starter" && existing.rsvp !== "no") {
    throw new Error("winterdoppel.lateJoiner.alreadyPlaying");
  }

  const anchor = rows.find((sp) => String(sp.player_id) === replaceId);
  if (!anchor || anchor.role !== "starter" || anchor.rsvp !== "no") {
    throw new Error("winterdoppel.lateJoiner.invalidReplacement");
  }
  if (Math.round((Number(anchor.cost_share) || 0) * 100) / 100 <= 0) {
    throw new Error("winterdoppel.lateJoiner.noTransferableCost");
  }

  const billing = await loadWinterDoppelSeriesBilling(series.id);
  const seriesSessions = await loadWinterDoppelSessions(series.id);
  const blocks = await loadWinterDoppelPlayerBlocks(players.map((p) => p.id));
  const allSeriesSp = await loadWinterDoppelSessionPlayers(
    seriesSessions.map((s) => s.id),
  );
  const fairnessContext = buildWinterDoppelFairnessContext({
    players,
    sessions: seriesSessions,
    sessionPlayers: allSeriesSp,
    blocks,
    fairnessMode: series.fairness_mode ?? series.fairnessMode,
  });
  const { forcedPairs, forcedGroups } =
    await loadForcedPlanningConstraints(series);

  const previousById = new Map(rows.map((sp) => [sp.id, { ...sp }]));
  const next = applyLateJoiner(rows, pid, replaceId, {
    locked: true,
    courtFee: Number(billing?.court_fee) || 0,
    courtCount: series.court_count,
    fairnessContext,
    ...billingCostOpts(series, billing, activeSessionCount(seriesSessions)),
  });
  applyPairingToRows(
    next,
    Math.max(1, Number(series.court_count) || 1),
    buildPartnerHistoryCounts(next),
    forcedPairs,
    forcedGroups,
    buildPlayerLevelMap(players),
  );

  const toInsert = next.filter((sp) => !sp?.id);
  if (toInsert.length) {
    const insertRows = toInsert.map((sp) => {
      const row = {
        session_id: sid,
        player_id: sp.player_id,
        role: sp.role,
        rsvp: sp.rsvp || "pending",
        court_no: sp.court_no,
        pair_slot: sp.pair_slot,
        waitlist_rank: sp.waitlist_rank,
        cost_share: sp.cost_share ?? 0,
        credit_share: sp.credit_share ?? 0,
        substitute_for_player_id: sp.substitute_for_player_id ?? null,
      };
      if (series.tenant_id) row.tenant_id = series.tenant_id;
      return row;
    });
    await insertSessionPlayerRows(insertRows);
    const reloaded = await loadWinterDoppelSessionPlayers([sid]);
    for (const sp of toInsert) {
      const saved = reloaded.find(
        (r) => String(r.player_id) === String(sp.player_id),
      );
      if (saved) sp.id = saved.id;
    }
  }

  await persistSessionPlayerRows(
    next.filter((sp) => sp?.id),
    { previousById },
  );

  await maybeApplyPublishedJoinCostsAfterPlay(series.id);

  return loadWinterDoppelSessionPlayers([sid]);
}

export async function recomputeWinterDoppelDraftCosts(seriesId) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series) throw new Error("winterdoppel.series.notFound");
  if (isWinterDoppelPlanFinal(series)) return [];
  await normalizeWinterDoppelDraftSessionStatuses(seriesId);
  const billing = await loadWinterDoppelSeriesBilling(seriesId);
  const courtFee = Number(billing?.court_fee) || 0;
  const sessions = await loadWinterDoppelSessions(seriesId);
  const active = sessions.filter((s) => s.status !== "cancelled");
  const sessionCount = active.length;
  const allSp = await loadWinterDoppelSessionPlayers(active.map((s) => s.id));
  const previousById = new Map(allSp.map((sp) => [sp.id, { ...sp }]));
  const costOpts = billingCostOpts(series, billing, sessionCount);
  assignAllDraftCostShares(allSp, courtFee, series.court_count, costOpts);
  await persistSessionPlayerRows(allSp, { previousById, fields: "costs" });
  return allSp;
}

/** Grobplanung-Kosten neu berechnen, Abdeckung prüfen, Planung bestätigen. */
export async function finalizeWinterDoppelSeriesPlan(seriesId) {
  const series = await getWinterDoppelSeries(seriesId);
  if (!series) throw new Error("winterdoppel.series.notFound");
  if (isWinterDoppelPlanFinal(series)) return series;

  await recomputeWinterDoppelDraftCosts(seriesId);
  const billing = await loadWinterDoppelSeriesBilling(seriesId);
  const sessions = await loadWinterDoppelSessions(seriesId);
  const active = sessions.filter((s) => s.status !== "cancelled");
  const allSp = await loadWinterDoppelSessionPlayers(active.map((s) => s.id));
  assertWinterDoppelSeasonCostCoverage(allSp, series, billing, sessions);
  if (__asStr(series.series_mode) === "training") {
    await syncWinterDoppelSeriesToTrainingsDesk(seriesId);
  }
  return finalizeWinterDoppelSeries(seriesId);
}

/**
 * Nach Veröffentlichung: Kosten 1/N auf Neuzugang umlegen, sobald er mitspielt.
 * @returns {{ transferred: number }}
 */
export async function applyWinterDoppelPublishedPlayerJoinCosts(
  seriesId,
  newPlayerId,
) {
  const sid = __asStr(seriesId);
  const pid = __asStr(newPlayerId);
  if (!sid || !pid) return { transferred: 0 };

  const series = await getWinterDoppelSeries(sid);
  if (!series || !isWinterDoppelPlanFinal(series)) {
    return { transferred: 0 };
  }

  const billing = await loadWinterDoppelSeriesBilling(sid);
  const players = await loadWinterDoppelPlayers(sid);
  if (!players.some((p) => String(p.id) === pid)) {
    throw new Error("winterdoppel.lateJoiner.playerNotInSeries");
  }

  const sessions = await loadWinterDoppelSessions(sid);
  const active = sessions.filter((s) => s.status !== "cancelled");
  const previous = await loadWinterDoppelSessionPlayers(
    active.map((s) => s.id),
  );
  const previousById = new Map(previous.map((sp) => [sp.id, { ...sp }]));

  const { sessionPlayers: next, transferred } =
    redistributeCostsForPublishedPlayerJoin({
      sessionPlayers: previous,
      sessions,
      newPlayerId: pid,
      playerCount: players.length,
    });

  if (transferred <= 0) return { transferred: 0 };

  await persistSessionPlayerRows(
    next.filter((sp) => sp?.id),
    { previousById, fields: "costs" },
  );

  const reloaded = await loadWinterDoppelSessionPlayers(
    active.map((s) => s.id),
  );
  assertWinterDoppelSeasonCostCoverage(reloaded, series, billing, sessions);
  return { transferred };
}

/** Sobald Post-Publish-Neuzugänger erstmals mitspielen: Saison-1/N buchen. */
async function maybeApplyPublishedJoinCostsAfterPlay(seriesId) {
  const sid = __asStr(seriesId);
  if (!sid) return;
  const series = await getWinterDoppelSeries(sid);
  if (!series || !isWinterDoppelPlanFinal(series)) return;

  const players = await loadWinterDoppelPlayers(sid);
  const sessions = await loadWinterDoppelSessions(sid);
  const active = sessions.filter((s) => s.status !== "cancelled");
  const sessionPlayers = await loadWinterDoppelSessionPlayers(
    active.map((s) => s.id),
  );
  const ready = listPostPublishJoinersReadyForSeasonCost({
    players,
    series,
    sessionPlayers,
  });
  for (const p of ready) {
    await applyWinterDoppelPublishedPlayerJoinCosts(sid, p.id);
  }
}

/**
 * Abend-Kostenanteile manuell setzen (nur Freies Spiel).
 * Presets: equal | singles2 | guestCash (Collector bekommt ½).
 */
export async function applyWinterDoppelSessionCostPreset(
  sessionId,
  { preset, collectorPlayerId = null, payerPlayerIds = null } = {},
) {
  const sid = __asStr(sessionId);
  if (!sid) throw new Error("winterdoppel.plan.notFound");

  const supabase = await getSupabaseClient();
  const { data: session, error: sessErr } = await supabase
    .from("winterdoppel_sessions")
    .select(SESSION_SELECT)
    .eq("id", sid)
    .maybeSingle();
  if (sessErr) throw sessErr;
  if (!session) throw new Error("winterdoppel.plan.notFound");

  const series = await getWinterDoppelSeries(session.series_id);
  if (!series) throw new Error("winterdoppel.series.notFound");
  const mode = __asStr(series.series_mode || series.seriesMode) || "free";
  if (mode !== "free") {
    throw new Error("winterdoppel.sessionCosts.freeModeOnly");
  }

  const billing = await loadWinterDoppelSeriesBilling(series.id);
  const seriesSessions = await loadWinterDoppelSessions(series.id);
  const rows = await loadWinterDoppelSessionPlayers([sid]);
  const previousById = new Map(rows.map((sp) => [sp.id, { ...sp }]));
  const evening = eveningTotal(
    Number(billing?.court_fee) || 0,
    series.court_count,
    billingCostOpts(series, billing, activeSessionCount(seriesSessions)),
  );

  const next = applySessionCostPresetRows(rows, {
    preset,
    eveningTotal: evening,
    collectorPlayerId,
    payerPlayerIds,
  });

  await persistSessionPlayerRows(
    next.filter((sp) => sp?.id),
    { previousById, fields: "costs" },
  );

  if (isWinterDoppelPlanFinal(series)) {
    const allSp = await loadWinterDoppelSessionPlayers(
      seriesSessions.map((s) => s.id),
    );
    assertWinterDoppelSeasonCostCoverage(
      allSp,
      series,
      billing,
      seriesSessions,
    );
  }

  return loadWinterDoppelSessionPlayers([sid]);
}

function assertWinterDoppelTrainingSeries(series) {
  if (__asStr(series?.series_mode || series?.seriesMode) !== "training") {
    throw new Error("winterdoppel.training.notTrainingSeries");
  }
}

async function syncPublishedTrainingDesk(series) {
  if (
    !series?.id ||
    !isWinterDoppelPlanFinal(series) ||
    __asStr(series.series_mode || series.seriesMode) !== "training"
  ) {
    return;
  }
  await syncWinterDoppelSeriesToTrainingsDesk(series.id, {
    updateExisting: true,
  });
}

export async function addWinterDoppelTrainingSessionStarter(
  sessionId,
  playerId,
) {
  const sid = __asStr(sessionId);
  const pid = __asStr(playerId);
  if (!sid || !pid) throw new Error("winterdoppel.plan.notFound");

  const supabase = await getSupabaseClient();
  const { data: session, error: sessErr } = await supabase
    .from("winterdoppel_sessions")
    .select(SESSION_SELECT)
    .eq("id", sid)
    .maybeSingle();
  if (sessErr) throw sessErr;
  if (!session) throw new Error("winterdoppel.plan.notFound");

  const series = await getWinterDoppelSeries(session.series_id);
  if (!series) throw new Error("winterdoppel.series.notFound");
  assertWinterDoppelTrainingSeries(series);

  const players = await loadWinterDoppelPlayers(series.id);
  if (!players.some((p) => String(p.id) === pid)) {
    throw new Error("winterdoppel.lateJoiner.playerNotInSeries");
  }

  const rows = await loadWinterDoppelSessionPlayers([sid]);
  const existing = rows.find((sp) => String(sp.player_id) === pid);
  if (existing?.role === "starter" && existing.rsvp !== "no") {
    throw new Error("winterdoppel.lateJoiner.alreadyPlaying");
  }
  if (existing) {
    const { error } = await supabase
      .from("winterdoppel_session_players")
      .update({ role: "starter", rsvp: "yes" })
      .eq("id", existing.id);
    if (error) throw error;
  } else {
    const row = {
      session_id: sid,
      player_id: pid,
      role: "starter",
      rsvp: "yes",
      cost_share: 0,
      credit_share: 0,
    };
    if (series.tenant_id) row.tenant_id = series.tenant_id;
    await insertSessionPlayerRows([row]);
  }
  await syncPublishedTrainingDesk(series);
  return loadWinterDoppelSessionPlayers([sid]);
}

export async function removeWinterDoppelTrainingSessionStarter(
  sessionPlayerId,
) {
  const id = __asStr(sessionPlayerId);
  if (!id) throw new Error("winterdoppel.plan.notFound");
  const supabase = await getSupabaseClient();
  const { data: current, error } = await supabase
    .from("winterdoppel_session_players")
    .select(SP_SELECT)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!current) throw new Error("winterdoppel.plan.notFound");

  const { data: session, error: sessErr } = await supabase
    .from("winterdoppel_sessions")
    .select(SESSION_SELECT)
    .eq("id", current.session_id)
    .maybeSingle();
  if (sessErr) throw sessErr;
  const series = session
    ? await getWinterDoppelSeries(session.series_id)
    : null;
  if (series) assertWinterDoppelTrainingSeries(series);

  await deleteSessionPlayersByIds([id]);
  if (series) await syncPublishedTrainingDesk(series);
  return true;
}

export async function rebuildWinterDoppelPublishedTrainingPlan(
  seriesId,
  opts = {},
) {
  const sid = __asStr(seriesId);
  const series = await getWinterDoppelSeries(sid);
  if (!series) throw new Error("winterdoppel.series.notFound");
  assertWinterDoppelTrainingSeries(series);
  if (!isWinterDoppelPlanFinal(series)) {
    await refreshWinterDoppelDraftPlan(sid);
    return loadWinterDoppelSessions(sid);
  }

  const fromDate = String(opts.fromDate || toIsoDate(new Date())).slice(0, 10);
  await ensureWinterDoppelSessions(sid, {
    allowPublished: true,
    protectBefore: fromDate,
  });
  const groups = await loadWinterDoppelPlayerGroups(sid);
  const schedules = trainingGroupSchedulesFromGroups(groups, series.season_end);
  const sessions = await loadWinterDoppelSessions(sid);
  const future = sessions.filter((s) => {
    const d = String(s.session_date || "").slice(0, 10);
    return d >= fromDate && s.status !== "cancelled";
  });
  const allSp = await loadWinterDoppelSessionPlayers(future.map((s) => s.id));
  /** @type {Map<string, object[]>} */
  const bySession = new Map();
  for (const sp of allSp) {
    const list = bySession.get(String(sp.session_id)) || [];
    list.push(sp);
    bySession.set(String(sp.session_id), list);
  }
  const playerSet = new Set(
    (await loadWinterDoppelPlayers(sid)).map((p) => String(p.id)),
  );

  const insertRows = [];
  const removeIds = [];
  const reviveIds = [];
  for (const session of future) {
    const expected = expectedTrainingPlayerIdsForDate(
      schedules,
      session.session_date,
    );
    for (const id of [...expected]) {
      if (!playerSet.has(id)) expected.delete(id);
    }
    const rows = bySession.get(String(session.id)) || [];
    const byPlayer = new Map(rows.map((sp) => [String(sp.player_id), sp]));
    const { remove, add } = diffTrainingSessionPlayers(rows, expected);
    for (const sp of remove) {
      if (sp.id) removeIds.push(sp.id);
    }
    for (const pid of add) {
      const existing = byPlayer.get(pid);
      if (existing?.id) {
        reviveIds.push(existing.id);
      } else {
        const row = {
          session_id: session.id,
          player_id: pid,
          role: "starter",
          rsvp: "yes",
          cost_share: 0,
          credit_share: 0,
        };
        if (series.tenant_id) row.tenant_id = series.tenant_id;
        insertRows.push(row);
      }
    }
  }
  if (removeIds.length) await deleteSessionPlayersByIds(removeIds);
  if (reviveIds.length) {
    const supabase = await getSupabaseClient();
    const { error } = await supabase
      .from("winterdoppel_session_players")
      .update({ role: "starter", rsvp: "yes" })
      .in("id", reviveIds);
    if (error) throw error;
  }
  if (insertRows.length) await insertSessionPlayerRows(insertRows);
  await syncPublishedTrainingDesk(series);
  return loadWinterDoppelSessions(sid);
}

export async function swapWinterDoppelPublishedTrainingWeeksOnce(
  seriesId,
  opts = {},
) {
  const sid = __asStr(seriesId);
  const series = await getWinterDoppelSeries(sid);
  if (!series) throw new Error("winterdoppel.series.notFound");
  assertWinterDoppelTrainingSeries(series);

  const fromDate = String(opts.fromDate || toIsoDate(new Date())).slice(0, 10);
  const groups = await loadWinterDoppelPlayerGroups(sid);
  const pairs = nextWinterDoppelOneOffWeekSwapPairs(
    groups.map((g) => ({
      ...g,
      schedule_end: g.schedule_end || series.season_end,
    })),
    fromDate,
  );
  if (!pairs.length) {
    throw new Error("winterdoppel.group.swapOnceUnavailable");
  }

  const sessions = await loadWinterDoppelSessions(sid);
  const byDate = new Map(
    sessions.map((s) => [String(s.session_date || "").slice(0, 10), s]),
  );
  for (const { dateA, dateB } of pairs) {
    const a = byDate.get(dateA);
    const b = byDate.get(dateB);
    if (!a?.id || !b?.id) continue;
    if (a.status === "cancelled" || b.status === "cancelled") continue;
    const rows = await loadWinterDoppelSessionPlayers([a.id, b.id]);
    const ids = rows.map((r) => r.id).filter(Boolean);
    const reinsert = rows.map((r) => {
      const nextSid = String(r.session_id) === String(a.id) ? b.id : a.id;
      const row = {
        session_id: nextSid,
        player_id: r.player_id,
        role: r.role,
        rsvp: r.rsvp,
        court_no: r.court_no,
        pair_slot: r.pair_slot,
        waitlist_rank: r.waitlist_rank,
        cost_share: r.cost_share ?? 0,
        credit_share: r.credit_share ?? 0,
        substitute_for_player_id: r.substitute_for_player_id ?? null,
      };
      if (series.tenant_id) row.tenant_id = series.tenant_id;
      return row;
    });
    if (ids.length) await deleteSessionPlayersByIds(ids);
    if (reinsert.length) await insertSessionPlayerRows(reinsert);
  }
  await syncPublishedTrainingDesk(series);
  return loadWinterDoppelSessions(sid);
}
